import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import Navbar from "./components/portfolio/Navbar";
import HeroSection from "./components/portfolio/HeroSection";

// Lazy load below-the-fold sections
const AboutSection = lazy(() => import("./components/portfolio/AboutSection"));
const SkillsSection = lazy(() => import("./components/portfolio/SkillsSection"));
const ProjectsSection = lazy(() => import("./components/portfolio/ProjectsSection"));
const TimelineSection = lazy(() => import("./components/portfolio/TimelineSection"));
const ContactSection = lazy(() => import("./components/portfolio/ContactSection"));
const Footer = lazy(() => import("./components/portfolio/Footer"));

// Lazy load Toaster
const LazyToaster = lazy(() =>
  import("sonner").then((mod) => ({ default: mod.Toaster }))
);

// Loading skeleton for lazy-loaded sections
const SectionSkeleton = ({ darkMode }) => (
  <div className={`py-24 md:py-32 ${darkMode ? "bg-[#0a0a0e]" : "bg-white"}`}>
    <div className="max-w-5xl mx-auto px-6">
      {/* Section header skeleton */}
      <div className="mb-16 space-y-3">
        <div className={`h-3 w-24 rounded-full ${
          darkMode ? "bg-white/[0.04]" : "bg-gray-100"
        }`} />
        <div className={`h-8 w-48 rounded-lg ${
          darkMode ? "bg-white/[0.04]" : "bg-gray-100"
        }`} />
      </div>
      {/* Content skeleton */}
      <div className="grid md:grid-cols-2 gap-8">
        <div className={`h-48 rounded-2xl ${
          darkMode ? "bg-white/[0.02]" : "bg-gray-50"
        }`} />
        <div className={`h-48 rounded-2xl ${
          darkMode ? "bg-white/[0.02]" : "bg-gray-50"
        }`} />
      </div>
    </div>
  </div>
);

function App() {
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  return (
    <div
      className={`min-h-screen transition-colors duration-500 ${
        darkMode ? "bg-[#09090b]" : "bg-white"
      }`}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <Navbar darkMode={darkMode} setDarkMode={setDarkMode} />
      <main>
        <HeroSection darkMode={darkMode} />
        <Suspense fallback={<SectionSkeleton darkMode={darkMode} />}>
          <AboutSection darkMode={darkMode} />
        </Suspense>
        <Suspense fallback={<SectionSkeleton darkMode={darkMode} />}>
          <SkillsSection darkMode={darkMode} />
        </Suspense>
        <Suspense fallback={<SectionSkeleton darkMode={darkMode} />}>
          <ProjectsSection darkMode={darkMode} />
        </Suspense>
        <Suspense fallback={<SectionSkeleton darkMode={darkMode} />}>
          <TimelineSection darkMode={darkMode} />
        </Suspense>
        <Suspense fallback={<SectionSkeleton darkMode={darkMode} />}>
          <ContactSection darkMode={darkMode} />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer darkMode={darkMode} />
      </Suspense>
      <Suspense fallback={null}>
        <LazyToaster richColors position="bottom-right" />
      </Suspense>
    </div>
  );
}

export default App;
